import { StatBlock, TimeStatBlock } from "@/components/StatBlock";
import React from "react";
import TitleBox from "@/components/TitleBox";
import ContinueButton from "@/components/Buttons/ContinueButton";
import { getUserStats, callWithAuth } from "@/api/Api";
import { GetPlantStatsResponse } from "@shared_types";
import { useQuery } from "@tanstack/react-query";
import { LessonStats } from "@/pages/UseLessonStats";
import { USER_STATS_QUERY_KEY } from "@/query_keys";
import PlantTree from "../components/Motivation/PlantTree";
import TreeGrowing from "../components/Motivation/TreeGrowing";
import { Button } from "@chakra-ui/react";
import { LoadingPage } from "./LoadingPage";

interface MotivationPageProps {
  title: React.ReactNode;
  subtitle: string | React.ReactNode;
  treeIncrease: boolean;
  isPlantingTree: boolean;
  plantStats: GetPlantStatsResponse;
  buttonText: string;
  buttonAction: () => void;
  lessonStats: LessonStats;
  repeatButtonAction?: () => void;
}

const formatNumber = (num: number) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "k";
  }
  return num.toString();
};

const MotivationPage: React.FC<MotivationPageProps> = ({
  title,
  subtitle,
  treeIncrease,
  isPlantingTree,
  plantStats,
  buttonText,
  buttonAction,
  lessonStats,
  repeatButtonAction,
}) => {
  const userStatsQuery = useQuery({
    queryKey: [USER_STATS_QUERY_KEY],
    queryFn: () => {
      return callWithAuth(getUserStats);
    },
  });

  if (userStatsQuery.isPending) {
    return <LoadingPage />;
  }

  const listeningTime = userStatsQuery.data?.listeningTime || 0;
  const wordsKnownCount = userStatsQuery.data?.wordsKnown || 0;
  const waterDropletsCount = userStatsQuery.data?.waterDropletsCount || 0;

  return (
    <div className="appPadding flex flex-col items-center justify-between w-full h-full">
      <div className="flex flex-row justify-center items-center w-full gap-2 pt-4">
        <TimeStatBlock
          timeSeconds={listeningTime}
          preText="⏰ "
          statDiff={lessonStats.listenTimeAdded}
        />
        <StatBlock
          value={formatNumber(wordsKnownCount)}
          preText="🇪🇸 "
          subtitle="Known Words"
          statDiff={lessonStats.knownWordsAdded}
        />
        <StatBlock
          value={formatNumber(waterDropletsCount)}
          preText="💧 "
          subtitle="Water Drops"
          statDiff={lessonStats.dropsAdded}
        />
      </div>
      {isPlantingTree ? (
        <div className="w-full h-full pt-10">
          <PlantTree plantStats={plantStats} buttonAction={buttonAction} />
        </div>
      ) : (
        <div className="flex flex-col flex-grow items-center justify-center w-full h-full">
          <div className="flex-grow flex w-full items-center justify-center">
            {treeIncrease ? (
              <TreeGrowing plantStats={plantStats} />
            ) : (
              <TitleBox
                title={title}
                subtitle={subtitle}
                classname="text-center items-center justify-center"
              />
            )}
          </div>
          <div className="w-full pb-2">
            <ContinueButton onClick={buttonAction} text={buttonText} />
            {repeatButtonAction && (
              <Button
                onClick={repeatButtonAction}
                variant={"ghost"}
                size={"sm"}
                fontSize={"var(--base-font-size-xs)"}
                fontWeight={"normal"}
                textDecoration={"underline"}
                color={"var(--color-grey)"}
              >
                Repeat this lesson
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MotivationPage;
